import { useEffect } from 'react';
import { useLijstStandStore, type LijstStand, type Weergave } from '@/stores/lijstStandStore';
import { isStandaard, klikOpKop, type Context, type Filterwaarde, type Lijst, type Sortering, type Startfilter } from '@xxtract/tabelfilters';

export interface TabelStandOpties {
  afhankelijkVan?: string;
  paginagrootte?: number;
  /** Startfilter uit een binnenkomende link (`useStartadres`); gaat voor op de bewaarde stand. */
  start?: Startfilter;
}

export interface TabelStand {
  filters: LijstStand['filters'];
  sortering: Sortering;
  pagina: number;
  paginagrootte?: number;
  weergave?: Weergave;
  isStandaard: boolean;
  zetFilter: (sleutel: string, waarde: Filterwaarde | undefined) => void;
  klikOpKop: (sleutel: string) => void;
  zetSortering: (sortering: Sortering) => void;
  zetPagina: (pagina: number) => void;
  zetPaginagrootte: (paginagrootte: number) => void;
  zetWeergave: (weergave: Weergave) => void;
  reset: () => void;
}

function isLeeg(w: Filterwaarde | undefined): boolean {
  if (w === undefined) return true;
  if (Array.isArray(w)) return w.length === 0;
  if (typeof w === 'string') return w === '';
  return !w.van && !w.tot;
}

/** Een binnenkomende link als lijststand: precies de link, rest standaard (§7). */
export function standUitStart(start: Startfilter): Partial<LijstStand> {
  return {
    filters: start.filters,
    sortering: start.sortering,
    pagina: start.pagina ?? 1,
    ...(start.paginagrootte ? { paginagrootte: start.paginagrootte } : {}),
  };
}

/**
 * Lijststand in het werkgeheugen (CAP-14): voorrang startfilter > bewaard > standaard. Elke filter- of
 * sorteerwijziging zet de pagina op 1; reset houdt paginagrootte, weergave en paginafilters.
 */
export function useTabelStand<R, C extends Context>(lijst: Lijst<R, C>, standSleutel: string, opties: TabelStandOpties = {}): TabelStand {
  const bewaard = useLijstStandStore((s) => s.standen[standSleutel]);
  const zetInStore = useLijstStandStore((s) => s.zet);
  const stand: LijstStand = {
    filters: {},
    sortering: [],
    pagina: 1,
    ...bewaard,
    ...(opties.start ? standUitStart(opties.start) : {}),
  };
  const paginagrootte = stand.paginagrootte ?? opties.paginagrootte;

  // Een stand die `useStartadres` schreef kent `afhankelijkVan` nog niet; eenmalig aanvullen, zodat een
  // paginafilter hem ook vóór de eerste eigen wijziging op pagina 1 zet.
  const ontbreektAfhankelijk = Boolean(opties.afhankelijkVan && bewaard && bewaard.afhankelijkVan !== opties.afhankelijkVan);
  useEffect(() => {
    if (ontbreektAfhankelijk) zetInStore(standSleutel, { afhankelijkVan: opties.afhankelijkVan });
  }, [ontbreektAfhankelijk, standSleutel, opties.afhankelijkVan, zetInStore]);
  const zet = (deel: Partial<LijstStand>) =>
    zetInStore(standSleutel, opties.afhankelijkVan ? { ...deel, afhankelijkVan: opties.afhankelijkVan } : deel);

  return {
    filters: stand.filters,
    sortering: stand.sortering,
    pagina: stand.pagina,
    paginagrootte,
    weergave: stand.weergave,
    isStandaard: isStandaard(stand.filters, stand.sortering),
    zetFilter: (sleutel, waarde) => {
      const { [sleutel]: _oud, ...rest } = stand.filters;
      zet({ filters: isLeeg(waarde) ? rest : { ...rest, [sleutel]: waarde! }, pagina: 1 });
    },
    klikOpKop: (sleutel) => {
      const kolom = lijst.kolommen.find((k) => k.sleutel === sleutel);
      if (kolom) zet({ sortering: klikOpKop(stand.sortering, kolom, lijst.maxSorteerkolommen ?? 5), pagina: 1 });
    },
    zetSortering: (sortering) => zet({ sortering, pagina: 1 }),
    zetPagina: (pagina) => zet({ pagina }),
    zetPaginagrootte: (grootte) => zet({ paginagrootte: grootte, pagina: 1 }),
    zetWeergave: (weergave) => zet({ weergave }),
    reset: () => zet({ filters: {}, sortering: [], pagina: 1 }),
  };
}
